import { getGetCurrentUserQueryKey, useUpdateCurrentUserUsername } from "@bvhi/backend-api/apis";
import { Button, LoadingButton, Stack } from "@bvhi/ui-base";
import { UpdateIcon } from "@bvhi/ui-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AnyObjectSchema, object, ref } from "yup";
import { FormBlockerDialog } from "../../../components/form/form-blocker-dialog";
import { FormWithActionCard } from "../../../components/form/form-with-action-card";
import { useApiErrorContext } from "../../../context/use-api-error-context";
import { DoubleSecuredConfirmDialog } from "../../../dialogs/double-secured-confirm-dialog";
import { useAuthentication } from "../../../hooks/use-authentication";
import { useToast } from "../../../hooks/use-toast";
import { useValidation } from "../../../hooks/use-validation";
import { UpdateUsernameCard } from "./components/update-username-card";

interface UpdatePasswordFormValues {
  username: string;
  usernameConfirmation: string;
}

export const UpdateUsernameView = () => {
  const { t } = useTranslation(["validation", "users", "errors", "profile", "common", "dialogs"]);
  const { setErrors } = useApiErrorContext();
  const { logout } = useAuthentication();
  const { userNameValidator } = useValidation();
  const queryClient = useQueryClient();
  const { pushUpdateSuccessToast, pushUpdateErrorToast } = useToast();
  const { mutate: updateCurrentUserUsername, isPending: isUpdateUserUsernamePending } = useUpdateCurrentUserUsername();

  const [securedUpdateDialogOpen, setSecuredUpdateDialogOpen] = useState<boolean>(false);

  const primaryButtonRef = useRef<HTMLButtonElement>(null);
  const onClose = useCallback(
    (confirmed: boolean) => {
      setSecuredUpdateDialogOpen(false);
      if (confirmed) primaryButtonRef.current?.click();
    },
    [primaryButtonRef, setSecuredUpdateDialogOpen]
  );

  const updateCurrentUserSchema: AnyObjectSchema = object({
    username: userNameValidator().required(t("validation:required")),
    usernameConfirmation: userNameValidator()
      .oneOf([ref("username")], t("validation:no.match"))
      .required(t("validation:required")),
  });

  const {
    formState: { isDirty, isSubmitSuccessful, ...otherFormState },
    handleSubmit,
    ...otherMethods
  } = useForm<UpdatePasswordFormValues>({
    resolver: yupResolver(updateCurrentUserSchema),
    mode: "all",
    defaultValues: {
      username: "",
      usernameConfirmation: "",
    },
  });

  const onSubmit: SubmitHandler<UpdatePasswordFormValues> = (data) => {
    updateCurrentUserUsername(
      { data: { username: data.username } },
      {
        onSuccess: () => {
          pushUpdateSuccessToast(t("users:users.user"));
          logout();
        },
        onError: (error) => {
          if (error.status && [400, 403, 409].includes(error.status)) {
            setErrors(error);
          }
          if (error.status === 500) pushUpdateErrorToast(t("users:users.user"));
        },
        onSettled: () => {
          void queryClient.invalidateQueries({ queryKey: getGetCurrentUserQueryKey() });
        },
      }
    );
  };

  return (
    <FormProvider formState={{ isDirty, isSubmitSuccessful, ...otherFormState }} handleSubmit={handleSubmit} {...otherMethods}>
      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)}>
        <Stack pt={2} spacing={2}>
          <FormWithActionCard
            contained
            actionCardContent={
              <LoadingButton
                disabled={!isDirty}
                loading={isUpdateUserUsernamePending}
                onClick={() => setSecuredUpdateDialogOpen(true)}
                startIcon={<UpdateIcon />}
              >
                {t("common:form.button.update", { value: t("profile:profile.current.user.username.entity") })}
              </LoadingButton>
            }
          >
            <UpdateUsernameCard />
          </FormWithActionCard>
          <Button ref={primaryButtonRef} sx={{ visibility: "hidden" }} type="submit" />
          <DoubleSecuredConfirmDialog
            onClose={onClose}
            open={securedUpdateDialogOpen}
            overwrite={{
              title: t("dialogs:dialogs.update.user.name.title"),
              confirmationCode: t("dialogs:dialogs.secured.update.dialog.form.confirm.text.field.placeholer"),
            }}
          />
        </Stack>
        <FormBlockerDialog isDirty={isDirty && !isSubmitSuccessful} />
      </form>
    </FormProvider>
  );
};
